/**
 * Unify Appointment FHIR model.
 *
 * Represents a scheduled event on a provider's calendar. Appointments fall
 * into two categories:
 *
 * - **Billable** — at least one `SBJ` patient participant + session type.
 *   Creates an Encounter, ChargeItem, and links to an EpisodeOfCare.
 * - **Non-billable** — provider meetings (no `SBJ`) or intake appointments
 *   (screening-call / consultation-call). No Encounter or ChargeItem.
 *
 * ┌──────────────────────────┬─────────────────────────────────────────────┐
 * │ FHIR field               │ Unify usage                                │
 * ├──────────────────────────┼─────────────────────────────────────────────┤
 * │ status                   │ proposed | booked | fulfilled | cancelled  │
 * │                          │ | noshow                                   │
 * │ start / end              │ ISO 8601 datetime with timezone            │
 * │ minutesDuration          │ Scheduled duration in minutes              │
 * │ serviceCategory          │ Highest-level grouping (CodeableConcept[]) │
 * │ serviceType              │ Mid-level service type (CodeableConcept[]) │
 * │ appointmentType          │ Modality: in-person | telehealth | hybrid  │
 * │ description              │ Free-text summary                          │
 * │ cancelationReason        │ CodeableConcept (cancellation-reason CS)   │
 * │ created                  │ When first booked                          │
 * │ slot                     │ Reference<Slot>[] — one per actor Schedule │
 * │ participant              │ PPRF (provider) + SBJ (patient) + others   │
 * ├──────────────────────────┼─────────────────────────────────────────────┤
 * │ EXTENSIONS               │                                            │
 * │ video-meeting-url        │ valueUrl — Zoom / video call link          │
 * │ recurring-pattern        │ valueCode — daily|weekly|biweekly|monthly  │
 * │ episode-of-care-context  │ valueReference → EpisodeOfCare             │
 * │ attendance-status        │ valueCode on participant — per-person      │
 * │ attendance-source        │ valueCoding — zoom-auto | manual           │
 * │ reservation-session      │ valueString — intake session ID            │
 * │ reservation-expires-at   │ valueDateTime — intake reservation expiry  │
 * └──────────────────────────┴─────────────────────────────────────────────┘
 *
 * Participant type codes (system: v3-ParticipationType):
 *   PPRF  — primary performer (Practitioner, exactly 1)
 *   SPRF  — secondary performer (Practitioner)
 *   SBJ   — billed client (Patient)
 *   PART  — supporting participant (RelatedPerson, never Patient)
 *   LOC   — practice office (Location)
 *   DEV   — portable equipment (Device)
 *
 * Participant type codes (system: unify participant-type):
 *   ROOM  — specific room (Location)
 *
 * Participant-level status: accepted | declined | tentative | needs-action
 *
 * Related tagging: linked QuestionnaireResponses carry a meta.tag with
 * system = APPOINTMENT_TAG_SYSTEM and code = Appointment.id.
 */
import type {
  Appointment,
  AppointmentParticipant,
  Coding,
  Extension,
  Reference,
} from '@medplum/fhirtypes';

import {
  ATTENDANCE_SOURCE_EXT,
  ATTENDANCE_STATUS_EXT,
  CANCELLATION_REASON_SYSTEM,
  EPISODE_OF_CARE_CONTEXT_EXT,
  RECURRING_PATTERN_EXT,
  RESERVATION_EXPIRES_AT_EXT,
  RESERVATION_SESSION_EXT,
  VIDEO_MEETING_URL_EXT,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal unions
// ═══════════════════════════════════════════════════════════════════════

/** Appointment-level status values used by Unify. */
export type AppointmentStatus =
  | 'proposed'
  | 'booked'
  | 'fulfilled'
  | 'cancelled'
  | 'noshow';

/** Recurring pattern codes. */
export type RecurringPattern =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly';

/** Cancellation reason codes (CodeSystem: cancellation-reason). */
export type CancellationReasonCode =
  | 'clinician-cancelled'
  | 'client-cancelled'
  | 'client-late-cancelled'
  | 'reservation-expired';

/** How attendance was determined (on the attendance-source extension). */
export type AttendanceSource = 'zoom-auto' | 'manual';

/** Participant-level status values (FHIR R4). */
export type ParticipantStatus =
  | 'accepted'
  | 'declined'
  | 'tentative'
  | 'needs-action';

// ═══════════════════════════════════════════════════════════════════════
//  Participant helpers
// ═══════════════════════════════════════════════════════════════════════

/** Find the first participant whose actor reference starts with the given resource type. */
function findParticipantByActorType(
  resource: Appointment,
  prefix: string,
): AppointmentParticipant | undefined {
  return resource.participant?.find((p) => p.actor?.reference?.startsWith(prefix));
}

/** All participants whose actor reference starts with the given resource type. */
function filterParticipantsByActorType(
  resource: Appointment,
  prefix: string,
): AppointmentParticipant[] {
  return resource.participant?.filter((p) => p.actor?.reference?.startsWith(prefix)) ?? [];
}

/** First Patient participant (SBJ — the billed client). */
export function getPatientParticipant(resource: Appointment): AppointmentParticipant | undefined {
  return findParticipantByActorType(resource, 'Patient/');
}

/** All Patient participants (relevant for group therapy with multiple SBJ). */
export function getPatientParticipants(resource: Appointment): AppointmentParticipant[] {
  return filterParticipantsByActorType(resource, 'Patient/');
}

/** First Practitioner participant (typically PPRF — primary performer). */
export function getPractitionerParticipant(resource: Appointment): AppointmentParticipant | undefined {
  return findParticipantByActorType(resource, 'Practitioner/');
}

/** All Practitioner participants (PPRF + SPRFs). */
export function getPractitionerParticipants(resource: Appointment): AppointmentParticipant[] {
  return filterParticipantsByActorType(resource, 'Practitioner/');
}

/** All RelatedPerson participants (PART — supporting family/couples members). */
export function getRelatedPersonParticipants(resource: Appointment): AppointmentParticipant[] {
  return filterParticipantsByActorType(resource, 'RelatedPerson/');
}

/**
 * Merge participants rebuilt by a scheduling form with the ones already stored.
 *
 * Scheduling forms rebuild `Appointment.participant` from their own fields, which
 * silently discards data they do not render. This keeps two things:
 *
 * 1. Per-participant extensions (attendance status, location) are carried forward
 *    onto matching actors, since scheduling never owns attendance.
 * 2. With `keepUnmatchedPatients`, `Patient` participants the form does not render
 *    are retained — the cohort roster of a group session, whose membership comes
 *    from the Group, not this form. Practitioners (primary facilitator plus
 *    co-facilitators), locations, and devices are all form-owned, so unmatched ones
 *    are dropped: a room change must not leave the old room booked, and removing a
 *    co-facilitator must actually remove them.
 */
export function mergeAppointmentParticipants(
  existing: AppointmentParticipant[] | undefined,
  formParticipants: AppointmentParticipant[],
  options: { keepUnmatchedPatients: boolean },
): AppointmentParticipant[] {
  const existingByRef = new Map<string, AppointmentParticipant>();
  for (const participant of existing ?? []) {
    const reference = participant.actor?.reference;
    if (reference) existingByRef.set(reference, participant);
  }

  const merged = formParticipants.map((participant) => {
    const reference = participant.actor?.reference;
    const prior = reference ? existingByRef.get(reference) : undefined;
    if (!prior?.extension?.length) return participant;
    return { ...participant, extension: prior.extension };
  });

  if (!options.keepUnmatchedPatients) return merged;

  const writtenRefs = new Set(
    formParticipants
      .map((p) => p.actor?.reference)
      .filter((reference): reference is string => Boolean(reference)),
  );
  const carriedOverPatients = (existing ?? []).filter((participant) => {
    const reference = participant.actor?.reference;
    return Boolean(reference) && !writtenRefs.has(reference as string)
      && (reference as string).startsWith('Patient/');
  });
  return [...merged, ...carriedOverPatients];
}

/** Attendance status extension on a participant entry (e.g. "attended", "no-show"). */
export function getParticipantAttendanceStatus(
  participant: AppointmentParticipant,
): string | undefined {
  return participant.extension?.find((e) => e.url === ATTENDANCE_STATUS_EXT)?.valueCode;
}

/** Set the attendance-status extension on a participant entry. */
export function setParticipantAttendanceStatus(
  participant: AppointmentParticipant,
  status: string | undefined,
): void {
  const others = (participant.extension ?? []).filter((e) => e.url !== ATTENDANCE_STATUS_EXT);
  participant.extension = status
    ? [...others, { url: ATTENDANCE_STATUS_EXT, valueCode: status }]
    : others.length ? others : undefined;
}

/** True for billed clients (Patient) and supporting session members (RelatedPerson). */
export function isClientAppointmentParticipant(participant: AppointmentParticipant): boolean {
  const ref = participant.actor?.reference ?? '';
  return ref.startsWith('Patient/') || ref.startsWith('RelatedPerson/');
}

/**
 * Mark every client participant declined with the given attendance-status.
 * Practitioner / location / room / device participants are left unchanged.
 */
export function declineClientParticipants(
  participants: AppointmentParticipant[] | undefined,
  attendanceStatus: 'clinician-cancelled',
): AppointmentParticipant[] {
  return (participants ?? []).map((participant) => {
    if (!isClientAppointmentParticipant(participant)) return participant;
    const next: AppointmentParticipant = { ...participant, status: 'declined' };
    setParticipantAttendanceStatus(next, attendanceStatus);
    return next;
  });
}

/** Clinician cancel: Appointment.status cancelled + all clients declined. */
export function applyClinicianCancelToAppointment(appointment: Appointment): Appointment {
  return {
    ...appointment,
    status: 'cancelled',
    cancelationReason: {
      coding: [
        {
          system: CANCELLATION_REASON_SYSTEM,
          code: 'clinician-cancelled',
          display: 'Clinician Cancelled',
        },
      ],
    },
    participant: declineClientParticipants(appointment.participant, 'clinician-cancelled'),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  Appointment-level extension getters
// ═══════════════════════════════════════════════════════════════════════

/** Zoom / video call link for telehealth appointments. */
export function getVideoMeetingUrl(resource: Appointment): string | undefined {
  return resource.extension?.find((e) => e.url === VIDEO_MEETING_URL_EXT)?.valueUrl;
}

/** Recurring pattern code (daily, weekly, biweekly, monthly). */
export function getRecurringPattern(resource: Appointment): RecurringPattern | undefined {
  return resource.extension?.find((e) => e.url === RECURRING_PATTERN_EXT)
    ?.valueCode as RecurringPattern | undefined;
}

/** EpisodeOfCare context reference (links appointment to an episode). */
export function getApptEocContextRef(resource: Appointment): Reference | undefined {
  return resource.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)?.valueReference;
}

/** Attendance source coding (zoom-auto or manual). */
export function getAttendanceSource(resource: Appointment): Coding | undefined {
  return resource.extension?.find((e) => e.url === ATTENDANCE_SOURCE_EXT)?.valueCoding;
}

/** Intake reservation session ID (present only on `proposed` intake appointments). */
export function getReservationSession(resource: Appointment): string | undefined {
  return resource.extension?.find((e) => e.url === RESERVATION_SESSION_EXT)?.valueString;
}

/** Intake reservation expiry timestamp. */
export function getReservationExpiresAt(resource: Appointment): string | undefined {
  return resource.extension?.find((e) => e.url === RESERVATION_EXPIRES_AT_EXT)?.valueDateTime;
}

/** Grace period after scheduled `end` before documentation / missing-note tasks apply. */
export const APPOINTMENT_POST_SESSION_DELAY_MS = 5 * 60 * 1000;

type AppointmentWindowFields = Pick<Appointment, 'start' | 'end' | 'minutesDuration'>;

/** Scheduled session end instant (ms): `end`, else `start` + `minutesDuration`. */
export function getAppointmentEndMs(resource: AppointmentWindowFields): number | undefined {
  if (resource.end) {
    const end = new Date(resource.end).getTime();
    if (!Number.isNaN(end)) return end;
  }
  if (resource.start && resource.minutesDuration != null && resource.minutesDuration > 0) {
    const start = new Date(resource.start).getTime();
    if (!Number.isNaN(start)) return start + resource.minutesDuration * 60_000;
  }
  return undefined;
}

/** True when now is past scheduled end (+ short delay). */
export function isFhirAppointmentPostSession(
  resource: AppointmentWindowFields,
  nowMs = Date.now(),
): boolean {
  const endMs = getAppointmentEndMs(resource);
  if (endMs == null) return false;
  return nowMs > endMs + APPOINTMENT_POST_SESSION_DELAY_MS;
}

// ═══════════════════════════════════════════════════════════════════════
//  Appointment-level extension setters (mutate in place)
// ═══════════════════════════════════════════════════════════════════════

/** Set or remove the video meeting URL extension. */
export function setVideoMeetingUrl(resource: Appointment, url: string | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== VIDEO_MEETING_URL_EXT);
  resource.extension = url
    ? [...others, { url: VIDEO_MEETING_URL_EXT, valueUrl: url }]
    : others.length ? others : undefined;
}

/** Set or remove the recurring pattern extension. */
export function setRecurringPattern(
  resource: Appointment,
  pattern: RecurringPattern | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RECURRING_PATTERN_EXT);
  resource.extension = pattern
    ? [...others, { url: RECURRING_PATTERN_EXT, valueCode: pattern }]
    : others.length ? others : undefined;
}

/**
 * Set or remove the episode-of-care-context extension.
 *
 * Also mirrors the episode reference into `supportingInformation[]` so the
 * standard `Appointment?supporting-info=` search parameter can scope
 * appointments by episode (Medplum has no custom SearchParameter support;
 * episode-scoped AccessPolicies rely on this denormalization).
 */
export function setApptEocContextRef(
  resource: Appointment,
  ref: Reference | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== EPISODE_OF_CARE_CONTEXT_EXT);
  resource.extension = ref
    ? [...others, { url: EPISODE_OF_CARE_CONTEXT_EXT, valueReference: ref }]
    : others.length ? others : undefined;

  const otherSupporting = (resource.supportingInformation ?? []).filter(
    (r) => !r.reference?.startsWith('EpisodeOfCare/'),
  );
  resource.supportingInformation = ref
    ? [...otherSupporting, ref]
    : otherSupporting.length ? otherSupporting : undefined;
}

/** Set or remove the attendance-source extension. */
export function setAttendanceSource(resource: Appointment, coding: Coding | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== ATTENDANCE_SOURCE_EXT);
  resource.extension = coding
    ? [...others, { url: ATTENDANCE_SOURCE_EXT, valueCoding: coding }]
    : others.length ? others : undefined;
}

/** Set or remove the reservation-session extension (intake flow). */
export function setReservationSession(resource: Appointment, sessionId: string | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RESERVATION_SESSION_EXT);
  resource.extension = sessionId
    ? [...others, { url: RESERVATION_SESSION_EXT, valueString: sessionId }]
    : others.length ? others : undefined;
}

/** Set or remove the reservation-expires-at extension (intake flow). */
export function setReservationExpiresAt(
  resource: Appointment,
  dateTime: string | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RESERVATION_EXPIRES_AT_EXT);
  resource.extension = dateTime
    ? [...others, { url: RESERVATION_EXPIRES_AT_EXT, valueDateTime: dateTime }]
    : others.length ? others : undefined;
}

/** Remove all reservation-related extensions (called on promotion to booked). */
export function clearReservationExtensions(resource: Appointment): void {
  resource.extension = (resource.extension ?? []).filter(
    (e) => e.url !== RESERVATION_SESSION_EXT && e.url !== RESERVATION_EXPIRES_AT_EXT,
  ) as Extension[] | undefined;
  if (resource.extension?.length === 0) resource.extension = undefined;
}
